import {
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    BackHandler,
    type View,
    Platform
} from "react-native";
import type {
    TreeViewKeyboardEventType,
    TreeViewStateConstantType,
    TreeViewCheckedState,
    TreeViewEntryType,
    ITreeNode
} from "./type";
import {
    getTreeViewNextCheckedKeys,
    TREE_VIEW_CONTEXT_MENU_KEY,
    getTreeViewTypeaheadIndex,
    getTreeViewCheckedStates,
    TREE_VIEW_ARROW_DOWN_KEY,
    getIsTreeViewKeyTarget,
    getTreeViewEnabledKeys,
    TREE_VIEW_ARROW_UP_KEY,
    getTreeViewErrorEntry,
    getTreeViewTabStopKey,
    focusTreeViewElement,
    getTreeViewEntries,
    getTreeViewNodeMap,
    getTreeViewElement,
    getTreeViewPath
} from "./util";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    usePointerType,
    useBreakpoint
} from "../../helpers/pointer";
import {
    getHorizontalStep
} from "../../helpers/direction";
import {
    formatNumber
} from "../../helpers/locale";

export const useTreeViewRowHover = () => {
    const rowRef = useRef<View | null>(null);

    const [
        isPointerInside,
        setIsPointerInside
    ] = useState(false);

    useEffect(() => {
        const element = getTreeViewElement(rowRef.current);

        if(!element) {
            return;
        }

        const onPointerEnter = (event: PointerEvent) => {
            setIsPointerInside(event.pointerType !== "touch");
        };

        const onPointerLeave = () => {
            setIsPointerInside(false);
        };

        element.addEventListener("pointerenter", onPointerEnter);

        element.addEventListener("pointerleave", onPointerLeave);

        return () => {
            element.removeEventListener("pointerenter", onPointerEnter);

            element.removeEventListener("pointerleave", onPointerLeave);
        };
    }, []);

    return {
        isPointerInside,
        rowRef
    };
};

const useTreeViewState = ({
    expandedKeys: expandedKeysProp,
    selectedKeys: selectedKeysProp,
    defaultExpandedKeys,
    defaultSelectedKeys,
    layoutBehaviour,
    checkBehaviour,
    maxIndentLevel,
    onLoadChildren,
    renderItemMenu,
    onDrillChange,
    selectionMode,
    durations,
    localize,
    onExpand,
    onSelect,
    onPress,
    locale,
    nodes
}: TreeViewStateConstantType) => {
    const breakpoint = useBreakpoint();

    const pointerType = usePointerType();

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isLazyEnabled = !!onLoadChildren;

    const [
        expandedState,
        setExpandedState
    ] = useState<Array<string>>(defaultExpandedKeys);

    const [
        selectedState,
        setSelectedState
    ] = useState<Array<string>>(defaultSelectedKeys);

    const [
        drillPath,
        setDrillPath
    ] = useState<Array<string>>([]);

    const [
        loadingKeys,
        setLoadingKeys
    ] = useState<Set<string>>(() => {
        const initial = getTreeViewEntries({
            expandedKeys: new Set(expandedKeysProp ?? defaultExpandedKeys),
            loadingKeys: new Set(),
            errorKeys: new Set(),
            isDrill: false,
            maxIndentLevel,
            isLazyEnabled,
            nodes
        });

        return new Set(initial.entries.filter((entry) => entry.isExpanded && entry.isLazy).map((entry) => entry.key));
    });

    const [
        errorKeys,
        setErrorKeys
    ] = useState<Set<string>>(() => new Set());

    const [
        focusedKey,
        setFocusedKey
    ] = useState<string | null>(null);

    const [
        menuKey,
        setMenuKey
    ] = useState<string | null>(null);

    const [
        announceKey,
        setAnnounceKey
    ] = useState<string | null>(null);

    const [
        announcement,
        setAnnouncement
    ] = useState<string>("");

    const itemRefs = useRef<Map<string, View | null>>(new Map());

    const requestedRef = useRef<Set<string>>(new Set());

    const pendingFocusRef = useRef<string | null>(null);

    const lastPressRef = useRef<{ key: string; time: number } | null>(null);

    const typeaheadRef = useRef<{ timer?: ReturnType<typeof setTimeout>; buffer: string }>({
        buffer: ""
    });

    const isMountedRef = useRef<boolean>(false);

    const expandedArray = expandedKeysProp ?? expandedState;

    const selectedArray = selectedKeysProp ?? selectedState;

    const expandedSet = useMemo(() => new Set(expandedArray), [
        expandedArray
    ]);

    const selectedSet = useMemo(() => new Set(selectedArray), [
        selectedArray
    ]);

    const nodeMap = useMemo(() => getTreeViewNodeMap(nodes), [
        nodes
    ]);

    const isDrill = layoutBehaviour === "drillDown" || layoutBehaviour === "auto" && (Platform.OS !== "web" || breakpoint === "compact");

    const activePathKeys = useMemo(() => {
        if(!isDrill) {
            return [];
        }

        const keys: Array<string> = [];

        for(const key of drillPath) {
            if(!nodeMap.has(key)) {
                break;
            }

            keys.push(key);
        }

        return keys;
    }, [
        drillPath,
        nodeMap,
        isDrill
    ]);

    const activePath = useMemo(() => getTreeViewPath({
        keys: activePathKeys,
        nodeMap
    }), [
        activePathKeys,
        nodeMap
    ]);

    const drillNode = activePath[activePath.length - 1] ?? null;

    const viewNodes = drillNode ? drillNode.children ?? [] : nodes;

    const {
        childrenByParent,
        entries
    } = useMemo(() => {
        const result = getTreeViewEntries({
            expandedKeys: expandedSet,
            nodes: viewNodes,
            maxIndentLevel,
            isLazyEnabled,
            loadingKeys,
            errorKeys,
            isDrill
        });

        if(drillNode && errorKeys.has(drillNode.key) && !drillNode.children?.length) {
            const errorEntry = getTreeViewErrorEntry({
                node: drillNode,
                level: 1
            });

            result.childrenByParent.set(null, [
                errorEntry
            ]);

            return {
                childrenByParent: result.childrenByParent,
                entries: [
                    errorEntry
                ]
            };
        }

        return result;
    }, [
        maxIndentLevel,
        isLazyEnabled,
        expandedSet,
        loadingKeys,
        viewNodes,
        drillNode,
        errorKeys,
        isDrill
    ]);

    const checkedStates = useMemo((): Map<string, TreeViewCheckedState> => {
        if(selectionMode !== "multiple") {
            return new Map();
        }

        return getTreeViewCheckedStates({
            selectedKeys: selectedSet,
            checkBehaviour,
            nodes
        });
    }, [
        selectionMode,
        checkBehaviour,
        selectedSet,
        nodes
    ]);

    const tabStopKey = getTreeViewTabStopKey({
        selectedKeys: selectedSet,
        focusedKey,
        entries
    });

    const isViewLoading = !!drillNode && !drillNode.children?.length && (!!drillNode.isLoading || loadingKeys.has(drillNode.key));

    const handleLoad = async (node: ITreeNode) => {
        if(!onLoadChildren) {
            return;
        }

        requestedRef.current.add(node.key);

        setLoadingKeys((current) => new Set(current).add(node.key));

        setErrorKeys((current) => {
            const next = new Set(current);

            next.delete(node.key);

            return next;
        });

        try {
            await onLoadChildren(node);

            if(isMountedRef.current) {
                setAnnounceKey(node.key);
            }
        } catch {
            if(isMountedRef.current) {
                setErrorKeys((current) => new Set(current).add(node.key));
            }
        } finally {
            if(isMountedRef.current) {
                setLoadingKeys((current) => {
                    const next = new Set(current);

                    next.delete(node.key);

                    return next;
                });
            }
        }
    };

    const handleExpandedChange = (keys: Array<string>, node: ITreeNode) => {
        if(!expandedKeysProp) {
            setExpandedState(keys);
        }

        onExpand?.(keys, node);
    };

    const handleSelectionChange = (keys: Array<string>, node: ITreeNode) => {
        if(!selectedKeysProp) {
            setSelectedState(keys);
        }

        onSelect?.(keys, node);
    };

    const handleExpand = (entry: TreeViewEntryType) => {
        if(entry.kind !== "node" || !entry.isBranch || entry.isDrillTarget || entry.isDisabled || entry.isExpanded) {
            return;
        }

        handleExpandedChange([
            ...expandedArray,
            entry.key
        ], entry.node);

        if(entry.isLazy && !entry.isLoading) {
            handleLoad(entry.node);
        }
    };

    const handleCollapse = (entry: TreeViewEntryType) => {
        if(entry.kind !== "node" || !entry.isExpanded) {
            return;
        }

        handleExpandedChange(expandedArray.filter((key) => key !== entry.key), entry.node);
    };

    const handleToggleExpand = (entry: TreeViewEntryType) => {
        if(entry.isExpanded) {
            handleCollapse(entry);

            return;
        }

        handleExpand(entry);
    };

    const handleExpandSiblings = (entry: TreeViewEntryType) => {
        const siblings = entries.filter((item) => item.kind === "node" && item.parentKey === entry.parentKey && item.isBranch && !item.isDrillTarget && !item.isDisabled && !item.isExpanded);

        if(!siblings.length) {
            return;
        }

        handleExpandedChange([
            ...expandedArray,
            ...siblings.map((item) => item.key)
        ], entry.node);

        siblings.forEach((item) => {
            if(item.isLazy && !item.isLoading) {
                handleLoad(item.node);
            }
        });
    };

    const handleToggleChecked = (entry: TreeViewEntryType) => {
        const isChecked = checkedStates.get(entry.key) !== "checked";

        handleSelectionChange(getTreeViewNextCheckedKeys({
            selectedKeys: selectedSet,
            node: entry.node,
            checkBehaviour,
            isChecked,
            nodes
        }), entry.node);
    };

    const handleSelectEntry = (entry: TreeViewEntryType) => {
        if(selectionMode === "multiple") {
            handleToggleChecked(entry);

            return;
        }

        if(selectionMode === "single" && !(selectedSet.size === 1 && selectedSet.has(entry.key))) {
            handleSelectionChange([
                entry.key
            ], entry.node);
        }
    };

    const handleRetry = (entry: TreeViewEntryType) => {
        handleLoad(entry.node);
    };

    const handleDrillIn = (entry: TreeViewEntryType) => {
        const nextKeys = [
            ...activePathKeys,
            entry.key
        ];

        setDrillPath(nextKeys);

        onDrillChange?.(getTreeViewPath({
            keys: nextKeys,
            nodeMap
        }));

        if(entry.isLazy && !requestedRef.current.has(entry.key)) {
            handleLoad(entry.node);
        }
    };

    const handleBack = () => {
        const lastKey = activePathKeys[activePathKeys.length - 1];

        if(!lastKey) {
            return;
        }

        const nextKeys = activePathKeys.slice(0, -1);

        pendingFocusRef.current = lastKey;

        setFocusedKey(lastKey);

        setDrillPath(nextKeys);

        onDrillChange?.(getTreeViewPath({
            keys: nextKeys,
            nodeMap
        }));
    };

    const handleLeafPress = (entry: TreeViewEntryType) => {
        if(selectionMode === "none" || pointerType === "coarse") {
            onPress?.(entry.node);

            return;
        }

        const now = Date.now();

        const last = lastPressRef.current;

        if(last && last.key === entry.key && now - last.time <= durations.longPress) {
            lastPressRef.current = null;

            onPress?.(entry.node);

            return;
        }

        lastPressRef.current = {
            key: entry.key,
            time: now
        };
    };

    const handlePressEntry = (entry: TreeViewEntryType) => {
        if(entry.kind === "error") {
            handleRetry(entry);

            return;
        }

        if(entry.isDisabled) {
            return;
        }

        if(entry.isDrillTarget) {
            handleDrillIn(entry);

            return;
        }

        if(selectionMode === "none" && entry.isBranch) {
            handleToggleExpand(entry);

            return;
        }

        handleSelectEntry(entry);

        if(!entry.isBranch) {
            handleLeafPress(entry);
        }
    };

    const handleActivateEntry = (entry: TreeViewEntryType) => {
        if(entry.kind === "error") {
            handleRetry(entry);

            return;
        }

        if(entry.isDisabled) {
            return;
        }

        if(entry.isDrillTarget) {
            handleDrillIn(entry);

            return;
        }

        if(selectionMode === "single") {
            handleSelectEntry(entry);
        }

        if(!entry.isBranch) {
            onPress?.(entry.node);

            return;
        }

        if(selectionMode !== "single") {
            handleToggleExpand(entry);
        }
    };

    const handleFocusKey = (key: string) => {
        setFocusedKey((current) => current === key ? current : key);
    };

    const handleFocusEntry = (entry: TreeViewEntryType) => {
        handleFocusKey(entry.key);

        focusTreeViewElement(itemRefs.current.get(entry.key));
    };

    const handleMenuOpenChange = (key: string, isOpen: boolean) => {
        setMenuKey((current) => {
            if(isOpen) {
                return key;
            }

            return current === key ? null : current;
        });
    };

    const handleLongPressEntry = (entry: TreeViewEntryType) => {
        if(entry.kind !== "node" || entry.isDisabled) {
            return;
        }

        setMenuKey(entry.key);
    };

    const handleExtendChecked = (from: TreeViewEntryType, to: TreeViewEntryType) => {
        const first = getTreeViewNextCheckedKeys({
            selectedKeys: selectedSet,
            isChecked: true,
            node: from.node,
            checkBehaviour,
            nodes
        });

        handleSelectionChange(getTreeViewNextCheckedKeys({
            selectedKeys: new Set(first),
            isChecked: true,
            checkBehaviour,
            node: to.node,
            nodes
        }), to.node);
    };

    const handleTypeahead = (key: string, startIndex: number) => {
        const typeahead = typeaheadRef.current;

        if(typeahead.timer) {
            clearTimeout(typeahead.timer);
        }

        typeahead.buffer = `${typeahead.buffer}${key}`;

        typeahead.timer = setTimeout(() => {
            typeahead.buffer = "";
        }, durations.typeahead);

        const index = getTreeViewTypeaheadIndex({
            query: typeahead.buffer,
            startIndex,
            entries,
            locale
        });

        const target = entries[index];

        if(target) {
            handleFocusEntry(target);
        }
    };

    const handleKeyDown = (event: TreeViewKeyboardEventType) => {
        if(event.altKey) {
            return;
        }

        const currentIndex = entries.findIndex((entry) => entry.key === tabStopKey);

        const current = entries[currentIndex];

        if(!current || !getIsTreeViewKeyTarget({
            node: itemRefs.current.get(current.key),
            target: event.target
        })) {
            return;
        }

        if(event.ctrlKey || event.metaKey) {
            if(event.key.toLowerCase() === "a" && selectionMode === "multiple") {
                event.preventDefault();

                handleSelectionChange(getTreeViewEnabledKeys(nodes), current.node);
            }

            return;
        }

        if(event.key === TREE_VIEW_CONTEXT_MENU_KEY || event.shiftKey && event.key === "F10") {
            if(current.kind === "node" && !current.isDisabled && renderItemMenu?.(current.node)) {
                event.preventDefault();

                setMenuKey(current.key);
            }

            return;
        }

        const step = getHorizontalStep({
            key: event.key,
            direction
        });

        if(step === 1) {
            event.preventDefault();

            const next = entries[currentIndex + 1];

            if(current.isExpanded && next && next.parentKey === current.key) {
                handleFocusEntry(next);
            } else if(current.kind === "node") {
                handleExpand(current);
            }

            return;
        }

        if(step === -1) {
            event.preventDefault();

            if(current.isExpanded) {
                handleCollapse(current);

                return;
            }

            const parent = entries.find((entry) => entry.key === current.parentKey);

            if(parent) {
                handleFocusEntry(parent);
            }

            return;
        }

        const isTypeaheadKey = Array.from(event.key).length === 1;

        if(event.key === " " && !typeaheadRef.current.buffer) {
            event.preventDefault();

            if(current.kind === "node" && !current.isDisabled && selectionMode !== "none") {
                handleSelectEntry(current);
            }

            return;
        }

        if(event.key === TREE_VIEW_ARROW_DOWN_KEY || event.key === TREE_VIEW_ARROW_UP_KEY) {
            event.preventDefault();

            const target = entries[currentIndex + (event.key === TREE_VIEW_ARROW_DOWN_KEY ? 1 : -1)];

            if(!target) {
                return;
            }

            if(event.shiftKey && selectionMode === "multiple") {
                handleExtendChecked(current, target);
            }

            handleFocusEntry(target);

            return;
        }

        if(event.key === "Home" || event.key === "End") {
            event.preventDefault();

            const target = event.key === "Home" ? entries[0] : entries[entries.length - 1];

            if(target) {
                handleFocusEntry(target);
            }

            return;
        }

        if(event.key === "Enter") {
            event.preventDefault();

            handleActivateEntry(current);

            return;
        }

        if(event.key === "*") {
            event.preventDefault();

            handleExpandSiblings(current);

            return;
        }

        if(isTypeaheadKey) {
            handleTypeahead(event.key, currentIndex);
        }
    };

    useEffect(() => {
        isMountedRef.current = true;

        const typeahead = typeaheadRef.current;

        return () => {
            isMountedRef.current = false;

            if(typeahead.timer) {
                clearTimeout(typeahead.timer);
            }
        };
    }, []);

    useEffect(() => {
        entries.forEach((entry) => {
            if(!entry.isExpanded || !entry.isLazy || entry.node.isLoading || requestedRef.current.has(entry.key)) {
                return;
            }

            handleLoad(entry.node);
        });
    }, [
        entries
    ]);

    useEffect(() => {
        if(!announceKey) {
            return;
        }

        const count = nodeMap.get(announceKey)?.node.children?.length;

        if(count === undefined) {
            return;
        }

        setAnnouncement(localize("tree-view-items-loaded", [
            formatNumber({
                value: count,
                locale
            })
        ]));

        setAnnounceKey(null);
    }, [
        announceKey,
        nodeMap
    ]);

    useEffect(() => {
        const key = pendingFocusRef.current;

        if(!key) {
            return;
        }

        const element = itemRefs.current.get(key);

        if(!element) {
            return;
        }

        pendingFocusRef.current = null;

        focusTreeViewElement(element);
    });

    useEffect(() => {
        if(Platform.OS !== "android" || !activePathKeys.length) {
            return;
        }

        const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
            handleBack();

            return true;
        });

        return () => {
            subscription.remove();
        };
    }, [
        activePathKeys
    ]);

    return {
        rootEntries: childrenByParent.get(null) ?? [],
        handleLongPressEntry,
        handleMenuOpenChange,
        handleToggleExpand,
        childrenByParent,
        handlePressEntry,
        handleFocusKey,
        checkedStates,
        handleKeyDown,
        isViewLoading,
        announcement,
        handleRetry,
        selectedSet,
        activePath,
        handleBack,
        tabStopKey,
        itemRefs,
        isDrill,
        menuKey,
        nodeMap,
        entries
    };
};
export default useTreeViewState;
